import { getLocale } from '@tov-plus/ui';
import { guestAppConfig } from '@tov-plus/config';
import {
  PlatformLanguageCode,
  platformDefaultLanguageCode,
} from '@tov-plus/domain';
import {
  TovButton,
  LocaleSwitcher,
  Select,
  SelectOption,
  useToast,
  getLanguageLabel,
  msg,
  TovCard,
} from '@tov-plus/ui';
import { CachedDataNotice, useOnlineActionGuard } from '@tov-plus/pwa';
import { startTransition, useEffect, useMemo, useState } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import {
  invalidateTovQuery,
  setTovQueryData,
  useTovMutation,
  useTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';

import { guestApi } from '../app/api';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { useGuestLiveUpdates } from '../lib/communications/useGuestLiveUpdates';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import {
  readGuestSnapshot,
  writeGuestSnapshot,
} from '../lib/offline/cacheSnapshot';

type GuestPreferenceState = {
  preferredLocaleCode: string | null;
  emailEnabled: boolean;
  inAppEnabled: boolean;
};

const guestPreferencesErrorMessage = () =>
  msg('pages.messages_page.guest_preferences_failed');

function getPreferenceState(
  preferences: Awaited<ReturnType<typeof guestApi.getCommunicationPreferences>>,
): GuestPreferenceState {
  return {
    preferredLocaleCode: preferences.preferredLocaleCode,
    emailEnabled:
      preferences.notificationPreferences.find(
        (preference) => preference.channelType === 'email',
      )?.isEnabled ?? true,
    inAppEnabled:
      preferences.notificationPreferences.find(
        (preference) => preference.channelType === 'in_app',
      )?.isEnabled ?? true,
  };
}

function getRouteLabel(href: string) {
  if (href === '/schedule') {
    return msg('general.modules.schedule.name');
  }
  if (href === '/rsvp') {
    return msg('general.modules.rsvp.name');
  }
  if (href === '/events') {
    return msg('general.modules.events.name');
  }
  if (href === '/locations') {
    return msg('general.modules.locations.name');
  }
  if (href === '/forms') {
    return msg('general.modules.forms.name');
  }
  if (href === '/help') {
    return msg('app.navigation.help');
  }

  return href === '/'
    ? msg('app.navigation.overview')
    : msg('pages.messages_page.linked_page');
}

export function MessagesPage() {
  const access = useGuestAccess();
  const liveUpdates = useGuestLiveUpdates(access);
  const notifications = useToast();
  const queryClient = useTovQueryClient();
  const guardOnlineAction = useOnlineActionGuard((message) => {
    notifications.error(message);
  });
  const [preferences, setPreferences] = useState<GuestPreferenceState>({
    preferredLocaleCode: platformDefaultLanguageCode,
    emailEnabled: true,
    inAppEnabled: true,
  });
  const [routeSyncAt, setRouteSyncAt] = useState<string | null>(null);
  const [offlineNotice, setOfflineNotice] = useState<string | null>(null);
  const readyOccasionSlug =
    access.status === 'ready' ? access.occasionSlug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const messagesQueryKey =
    readyOccasionSlug && readyGuestToken
      ? guestQueryKeys.messages(readyOccasionSlug, readyGuestToken)
      : null;
  const preferencesQuery = useTovQuery({
    queryKey:
      messagesQueryKey ??
      guestQueryKeys.messages('unknown-occasion', 'unknown-token'),
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
    queryFn: () => {
      if (!readyOccasionSlug || !readyGuestToken) {
        throw new Error(guestPreferencesErrorMessage());
      }
      return guestApi.getCommunicationPreferences(
        readyOccasionSlug,
        readyGuestToken,
      );
    },
  });
  const messagingInboxQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken
        ? guestQueryKeys.messagingInbox(readyOccasionSlug, readyGuestToken)
        : guestQueryKeys.messagingInbox('unknown-occasion', 'unknown-token'),
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
    queryFn: () => {
      if (!readyOccasionSlug || !readyGuestToken) {
        throw new Error(guestPreferencesErrorMessage());
      }
      return guestApi.getMessagingInbox(readyOccasionSlug, readyGuestToken);
    },
  });

  const markThreadReadMutation = useTovMutation({
    mutationFn: (input: {
      threadId: string;
      lastReadMessageId?: string | null;
    }) => {
      if (!readyOccasionSlug || !readyGuestToken) {
        throw new Error(guestPreferencesErrorMessage());
      }
      return guestApi.postMessagingThreadRead(
        readyOccasionSlug,
        readyGuestToken,
        input.threadId,
        input.lastReadMessageId ?? null,
      );
    },
    onSuccess() {
      if (readyOccasionSlug && readyGuestToken) {
        void invalidateTovQuery(
          queryClient,
          guestQueryKeys.messagingInbox(readyOccasionSlug, readyGuestToken),
        );
        void invalidateTovQuery(
          queryClient,
          guestQueryKeys.bootstrap(readyOccasionSlug, readyGuestToken),
        );
      }
      access.refresh();
    },
  });

  const savePreferencesMutation = useTovMutation({
    mutationFn: () => {
      if (!readyOccasionSlug || !readyGuestToken) {
        throw new Error(guestPreferencesErrorMessage());
      }
      return guestApi.updateCommunicationPreferences(
        readyOccasionSlug,
        readyGuestToken,
        {
          preferred_locale_code: preferences.preferredLocaleCode,
          email_enabled: preferences.emailEnabled,
          in_app_enabled: preferences.inAppEnabled,
        },
      );
    },
    onSuccess(updated) {
      if (messagesQueryKey) {
        setTovQueryData(queryClient, messagesQueryKey, updated);
      }
      setPreferences(getPreferenceState(updated));
      access.refresh();
      notifications.success(
        msg('pages.messages_page.communication_preferences_saved'),
      );
    },
    onError(error) {
      notifications.error(
        msg('pages.messages_page.preference_save_failed'),
        getGuestErrorMessage(error, guestPreferencesErrorMessage()),
      );
    },
    invalidate:
      readyOccasionSlug && readyGuestToken
        ? [
            guestQueryKeys.messages(readyOccasionSlug, readyGuestToken),
            guestQueryKeys.bootstrap(readyOccasionSlug, readyGuestToken),
          ]
        : [],
  });

  useEffect(() => {
    if (access.status !== 'ready') {
      return;
    }

    const cacheKey = `tov-plus:guest-messages:${access.occasionSlug}:${access.guestToken}`;

    async function syncRouteState() {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const cached = await readGuestSnapshot<{ cachedAt: string }>(cacheKey);
        startTransition(() => {
          setRouteSyncAt(cached?.cachedAt ?? null);
          setOfflineNotice(
            cached?.cachedAt
              ? msg(
                  'pages.messages_page.offline_mode_message_state_may_be_stale_until_the_device_reconnects',
                )
              : msg(
                  'pages.messages_page.offline_mode_no_cached_message_freshness_marker_is_available_yet',
                ),
          );
        });
        return;
      }

      const cachedAt = new Date().toISOString();
      await writeGuestSnapshot(cacheKey, { cachedAt });
      startTransition(() => {
        setRouteSyncAt(cachedAt);
        setOfflineNotice(null);
      });
    }

    void syncRouteState();
  }, [access]);

  useEffect(() => {
    if (!preferencesQuery.data) {
      return;
    }
    startTransition(() => {
      setPreferences(getPreferenceState(preferencesQuery.data));
    });
  }, [preferencesQuery.data]);

  const communicationSummary = useMemo(() => {
    if (access.status !== 'ready') {
      return null;
    }

    return {
      messageCount: access.experience.recentCommunications.length,
      deliveredCount: access.experience.recentCommunications.filter(
        (item) => item.delivery.status === 'delivered',
      ).length,
      linkedEvents: new Set(
        access.experience.recentCommunications
          .map((item) => item.event?.id)
          .filter(Boolean),
      ).size,
    };
  }, [access]);
  const routeSummary = useMemo(() => {
    if (access.status !== 'ready') {
      return [];
    }

    const counts = new Map<string, number>();

    for (const item of access.experience.recentCommunications) {
      const key =
        item.deepLink ?? (item.event ? `/events/${item.event.id}` : '/');
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return Array.from(counts.entries())
      .map(([href, count]) => ({
        href,
        count,
        label: getRouteLabel(href),
      }))
      .sort((left, right) => right.count - left.count);
  }, [access]);

  if (access.status !== 'ready') {
    return null;
  }

  const resolvedLocaleCode = getLocale();
  const occasionTimezone = access.experience.occasion.timezone;

  const unreadDeliveryIds = new Set(
    liveUpdates.unreadItems.map((item) => item.delivery.id),
  );
  const messagingInbox = messagingInboxQuery.data ?? [];
  const unreadThreads = messagingInbox.filter((item) => item.unreadCount > 0);
  const serverUnreadCount = unreadThreads.reduce(
    (total, item) => total + item.unreadCount,
    0,
  );
  const priorityMessages = access.experience.recentCommunications.filter(
    (item) =>
      unreadDeliveryIds.has(item.delivery.id) ||
      item.deepLink === '/schedule' ||
      item.deepLink === '/events',
  );
  const routeInbox = routeSummary.slice(0, 4);
  const deliveredMessages = access.experience.recentCommunications.filter(
    (item) => item.delivery.status === 'delivered',
  );
  const reviewedCount =
    access.experience.recentCommunications.length - unreadDeliveryIds.size;
  const criticalRoutes = new Set(
    priorityMessages
      .map(
        (item) =>
          item.deepLink ?? (item.event ? `/events/${item.event.id}` : '/'),
      )
      .filter(Boolean),
  );
  const supportReplies = access.experience.recentCommunications.filter(
    (item) => item.message?.communicationEventKey === 'guest.support.reply',
  );

  function handleSavePreferences() {
    if (!readyOccasionSlug || !readyGuestToken) {
      return;
    }

    if (!guardOnlineAction()) {
      return;
    }

    savePreferencesMutation.mutate();
  }

  function markMessagingThreadRead(
    threadId: string,
    messageId?: string | null,
  ) {
    if (!guardOnlineAction()) {
      return;
    }

    markThreadReadMutation.mutate({
      threadId,
      lastReadMessageId: messageId ?? null,
    });
  }

  function markAllMessagingThreadsRead() {
    if (!guardOnlineAction()) {
      return;
    }

    for (const item of unreadThreads) {
      markThreadReadMutation.mutate({
        threadId: item.thread.id,
        lastReadMessageId: item.latestMessage?.id ?? null,
      });
    }
  }

  return (
    <section className="page-grid">
      <TovCard variant="content" as="div" isWide className="hero-card">
        <p className="page-eyebrow">{msg('general.modules.messages.name')}</p>
        <h2>
          {msg(
            'pages.messages_page.pages_messages_page_occasion_communications',
          )}
        </h2>
        <p>
          {msg(
            'pages.messages_page.pages_messages_page_review_planner_updates_tied_to_your_invite_manage',
          )}{' '}
        </p>
        <div className="hero-card__stats">
          <article>
            <strong>{communicationSummary?.messageCount ?? 0}</strong>
            <span>
              {msg('pages.messages_page.pages_messages_page_visible_update')}
            </span>
          </article>
          <article>
            <strong>{communicationSummary?.deliveredCount ?? 0}</strong>
            <span>
              {msg('pages.messages_page.pages_messages_page_delivered_message')}
            </span>
          </article>
          <article>
            <strong>{communicationSummary?.linkedEvents ?? 0}</strong>
            <span>
              {msg(
                'pages.messages_page.pages_messages_page_event_with_communication',
              )}
            </span>
          </article>
          <article>
            <strong>{serverUnreadCount}</strong>
            <span>
              {msg('pages.messages_page.pages_messages_page_unread_update')}
            </span>
          </article>
        </div>
        {routeSyncAt ? (
          <p>
            {msg('pages.events_page.pages_events_page_last_synced')}{' '}
            {formatGuestDateTime(
              routeSyncAt,
              resolvedLocaleCode,
              occasionTimezone,
            )}
            {offlineNotice ? ` • ${offlineNotice}` : ''}
          </p>
        ) : offlineNotice ? (
          <p>{offlineNotice}</p>
        ) : null}
        <CachedDataNotice cachedValue={access.experience} />
        {serverUnreadCount > 0 ? (
          <div className="action-row">
            <TovButton
              variant="secondary"
              onClick={() => markAllMessagingThreadsRead()}
              disabled={markThreadReadMutation.isPending}
            >
              {msg(
                'pages.messages_page.pages_messages_page_mark_all_as_reviewed',
              )}{' '}
            </TovButton>
          </div>
        ) : null}
      </TovCard>

      <TovCard variant="content" as="div">
        <p className="page-eyebrow">
          {msg('pages.messages_page.pages_messages_page_preferences')}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg(
                'pages.messages_page.pages_messages_page_interface_language',
              )}
            </strong>
            <span>
              {msg(
                'pages.messages_page.pages_messages_page_controls_this_device_apos_s_guest_shell_language',
              )}
            </span>
            <LocaleSwitcher />
          </article>
          <article>
            <strong>
              {msg(
                'pages.messages_page.pages_messages_page_communication_language',
              )}
            </strong>
            <span>
              {msg(
                'pages.messages_page.pages_messages_page_saved_to_your_invite_so_future_planner_messages',
              )}{' '}
            </span>
            <Select
              aria-label={msg(
                'pages.messages_page.pages_messages_page_communication_language',
              )}
              value={
                preferences.preferredLocaleCode ?? platformDefaultLanguageCode
              }
              onChange={(event) =>
                setPreferences((current) => ({
                  ...current,
                  preferredLocaleCode: event.target.value,
                }))
              }
            >
              <SelectOption value={PlatformLanguageCode.English}>
                {getLanguageLabel(PlatformLanguageCode.English)}
              </SelectOption>
              <SelectOption value={PlatformLanguageCode.Hebrew}>
                {getLanguageLabel(PlatformLanguageCode.Hebrew)}
              </SelectOption>
            </Select>
          </article>
          <article>
            <strong>
              {msg('pages.messages_page.pages_messages_page_email_updates')}
            </strong>
            <span>
              {preferences.emailEnabled
                ? msg(
                    'pages.messages_page.pages_messages_page_planner_emails_are_enabled_for_this_invite',
                  )
                : msg(
                    'pages.messages_page.pages_messages_page_planner_emails_are_disabled_for_this_invite',
                  )}
            </span>
            <TovButton
              variant="secondary"
              onClick={() =>
                setPreferences((current) => ({
                  ...current,
                  emailEnabled: !current.emailEnabled,
                }))
              }
            >
              {preferences.emailEnabled
                ? msg('pages.messages_page.pages_messages_page_disable_email')
                : msg('pages.messages_page.pages_messages_page_enable_email')}
            </TovButton>
          </article>
          <article>
            <strong>
              {msg('pages.messages_page.pages_messages_page_in_app_updates')}
            </strong>
            <span>
              {preferences.inAppEnabled
                ? msg(
                    'pages.messages_page.pages_messages_page_message_center_updates_stay_enabled',
                  )
                : msg(
                    'pages.messages_page.pages_messages_page_message_center_updates_are_disabled',
                  )}
            </span>
            <TovButton
              variant="secondary"
              onClick={() =>
                setPreferences((current) => ({
                  ...current,
                  inAppEnabled: !current.inAppEnabled,
                }))
              }
            >
              {preferences.inAppEnabled
                ? msg('pages.messages_page.pages_messages_page_disable_in_app')
                : msg('pages.messages_page.pages_messages_page_enable_in_app')}
            </TovButton>
          </article>
        </div>
        <div className="action-row">
          <TovButton
            onClick={() => void handleSavePreferences()}
            disabled={savePreferencesMutation.isPending}
          >
            {savePreferencesMutation.isPending
              ? msg('components.shell.components_shell_saving')
              : msg('pages.messages_page.pages_messages_page_save_preferences')}
          </TovButton>
          <TovButton asChild variant="secondary">
            <NavLink to="/access">
              {msg('app.router.app_router_review_access')}
            </NavLink>
          </TovButton>
        </div>
      </TovCard>

      <TovCard variant="content" as="div" isWide>
        <p className="page-eyebrow">
          {msg('pages.messages_page.pages_messages_page_support_replies')}
        </p>
        {supportReplies.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {supportReplies.map((item) => (
              <article key={`support-reply:${item.delivery.id}`}>
                <strong>
                  {item.message?.subjectText ??
                    msg(
                      'pages.messages_page.pages_messages_page_planner_support_reply',
                    )}
                </strong>
                <span>
                  {item.message?.bodyText ??
                    msg(
                      'pages.messages_page.pages_messages_page_a_planner_replied_to_a_guest_support_request',
                    )}
                </span>
                <span>
                  {item.message?.bodyText
                    ? msg(
                        'pages.messages_page.pages_messages_page_open_help_if_you_need_to_continue_the_same',
                      )
                    : msg(
                        'pages.messages_page.pages_messages_page_a_planner_replied_to_a_saved_support_request',
                      )}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.messages_page.pages_messages_page_no_planner_replies_to_guest_support_requests_are',
            )}
          </p>
        )}
      </TovCard>

      {priorityMessages.length > 0 ? (
        <TovCard variant="content" as="div" isWide>
          <p className="page-eyebrow">
            {msg('pages.messages_page.pages_messages_page_attention_now')}
          </p>
          <h3>
            {msg(
              'pages.messages_page.pages_messages_page_critical_guest_notices',
            )}
          </h3>
          <p>
            {msg(
              'pages.messages_page.pages_messages_page_these_updates_are_either_still_unread_or_point',
            )}{' '}
          </p>
          <div className="detail-list detail-list--stacked">
            {priorityMessages.slice(0, 4).map((item) => {
              const target =
                item.deepLink ??
                (item.event ? `/events/${item.event.id}` : '/');
              return (
                <article key={`priority:${item.delivery.id}`}>
                  <strong>
                    {item.message?.subjectText ??
                      item.event?.displayName ??
                      msg(
                        'pages.messages_page.pages_messages_page_guest_update',
                      )}
                  </strong>
                  <span>
                    {unreadDeliveryIds.has(item.delivery.id)
                      ? msg(
                          'pages.messages_page.pages_messages_page_unread_notice',
                        )
                      : msg(
                          'pages.messages_page.pages_messages_page_review_again_if_plans_changed',
                        )}
                    {item.event ? ` • ${item.event.displayName}` : ''}
                  </span>
                  <span>
                    {item.message?.bodyText ??
                      msg(
                        'pages.messages_page.pages_messages_page_open_the_linked_surface_for_the_latest_guest_facing',
                      )}
                  </span>
                  <div className="action-row">
                    <TovButton asChild>
                      <NavLink to={target}>
                        {msg('general.actions.open')} {getRouteLabel(target)}
                      </NavLink>
                    </TovButton>
                    {unreadDeliveryIds.has(item.delivery.id) ? (
                      <TovButton
                        variant="secondary"
                        onClick={() => liveUpdates.markRead(item.delivery.id)}
                      >
                        {msg(
                          'pages.messages_page.pages_messages_page_mark_reviewed',
                        )}{' '}
                      </TovButton>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </TovCard>
      ) : null}

      <TovCard variant="content" as="div">
        <p className="page-eyebrow">
          {msg('pages.messages_page.pages_messages_page_next_steps')}
        </p>
        <div className="detail-list">
          {routeInbox.map((item) => (
            <article key={item.href}>
              <strong>{item.label}</strong>
              <span>
                {item.count}{' '}
                {msg(
                  'pages.messages_page.pages_messages_page_message_linked_route',
                )}
                {item.count === 1 ? '' : 's'}
              </span>
            </article>
          ))}
          <article>
            <strong>
              {msg('pages.messages_page.pages_messages_page_critical_surfaces')}
            </strong>
            <span>
              {criticalRoutes.size > 0
                ? `${criticalRoutes.size} guest route${criticalRoutes.size === 1 ? '' : 's'} currently carry the most urgent message traffic.`
                : msg(
                    'pages.messages_page.pages_messages_page_no_urgent_route_focus_right_now',
                  )}
            </span>
          </article>
        </div>
        <div className="action-row">
          <TovButton asChild>
            <NavLink to="/schedule">
              {`${msg('general.actions.open')} ${msg('general.modules.schedule.name')}`}
            </NavLink>
          </TovButton>
          <TovButton asChild variant="secondary">
            <NavLink to="/help">
              {msg('pages.messages_page.pages_messages_page_get_help')}
            </NavLink>
          </TovButton>
          <TovButton asChild variant="secondary">
            <a href={`mailto:${guestAppConfig.supportEmail}`}>
              {msg('app.router.app_router_contact_support')}{' '}
            </a>
          </TovButton>
        </div>
        <p>
          {msg(
            'pages.messages_page.pages_messages_page_message_links_are_a_shortcut_back_into_the',
          )}{' '}
        </p>
      </TovCard>

      <TovCard variant="content" as="div">
        <p className="page-eyebrow">
          {msg('pages.messages_page.pages_messages_page_notification_health')}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg('pages.messages_page.pages_messages_page_reviewed_receipt')}
            </strong>
            <span>
              {reviewedCount}{' '}
              {msg('pages.messages_page.pages_messages_page_update')}
              {reviewedCount === 1 ? '' : 's'}{' '}
              {msg(
                'pages.messages_page.pages_messages_page_have_already_been_reviewed_in_this_guest_session',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.messages_page.pages_messages_page_unread_notices')}
            </strong>
            <span>
              {serverUnreadCount > 0
                ? `${serverUnreadCount} update${serverUnreadCount === 1 ? '' : 's'} still need review in this message center.`
                : msg(
                    'pages.messages_page.pages_messages_page_all_visible_updates_have_been_reviewed_on_this_device',
                  )}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.messages_page.pages_messages_page_delivered_successfully',
              )}
            </strong>
            <span>
              {deliveredMessages.length}{' '}
              {msg('pages.forms_page.pages_forms_page_of')}{' '}
              {access.experience.recentCommunications.length}{' '}
              {msg(
                'pages.messages_page.pages_messages_page_visible_communication',
              )}{' '}
              {access.experience.recentCommunications.length === 1 ? '' : 's'}{' '}
              {msg(
                'pages.messages_page.pages_messages_page_reached_delivered_state',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.messages_page.pages_messages_page_best_review_order')}
            </strong>
            <span>
              {msg(
                'pages.messages_page.pages_messages_page_start_with_schedule_and_events_then_review_travel',
              )}{' '}
            </span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content" as="div" isWide>
        <p className="page-eyebrow">
          {msg(
            'pages.messages_page.pages_messages_page_visible_message_history',
          )}
        </p>
        {messagingInbox.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {messagingInbox.map((item) => (
              <article key={`thread:${item.thread.id}`}>
                <strong>
                  {item.thread.subject ??
                    item.latestMessage?.senderDisplayName ??
                    msg('pages.help_page.pages_help_page_occasion_update')}
                </strong>
                <span>
                  {item.unreadCount}{' '}
                  {msg('pages.messages_page.pages_messages_page_unread_update')}
                </span>
                <span>
                  {item.latestMessagePreview ??
                    item.latestMessage?.body ??
                    msg(
                      'pages.messages_page.pages_messages_page_no_visible_message_body_was_included',
                    )}
                </span>
                <div className="action-row">
                  {item.unreadCount > 0 ? (
                    <TovButton
                      variant="secondary"
                      onClick={() =>
                        markMessagingThreadRead(
                          item.thread.id,
                          item.latestMessage?.id,
                        )
                      }
                      disabled={markThreadReadMutation.isPending}
                    >
                      {msg(
                        'pages.messages_page.pages_messages_page_mark_reviewed',
                      )}
                    </TovButton>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        ) : null}
        {access.experience.recentCommunications.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {access.experience.recentCommunications.map((item) => (
              <article key={item.delivery.id}>
                <strong>
                  {item.message?.subjectText ??
                    item.event?.displayName ??
                    msg('pages.help_page.pages_help_page_occasion_update')}
                </strong>
                <span>
                  {item.delivery.status.replaceAll('_', ' ')}
                  {item.event ? ` • ${item.event.displayName}` : ''}
                </span>
                <span>
                  {msg('components.shell.sent')}{' '}
                  {formatGuestDateTime(
                    item.delivery.queuedAt,
                    resolvedLocaleCode,
                    occasionTimezone,
                  )}
                  {item.delivery.deliveredAt
                    ? ` • delivered ${formatGuestDateTime(
                        item.delivery.deliveredAt,
                        resolvedLocaleCode,
                        occasionTimezone,
                      )}`
                    : ''}
                </span>
                <span>
                  {item.message?.bodyText ??
                    msg(
                      'pages.messages_page.pages_messages_page_no_visible_message_body_was_included',
                    )}
                </span>
                <span>
                  {unreadDeliveryIds.has(item.delivery.id)
                    ? msg(
                        'pages.messages_page.pages_messages_page_new_since_your_last_review',
                      )
                    : msg(
                        'pages.messages_page.pages_messages_page_already_reviewed',
                      )}
                </span>
                <div className="action-row">
                  {item.deepLink ? (
                    <TovButton asChild variant="secondary">
                      <NavLink to={item.deepLink}>
                        {msg(
                          'pages.messages_page.pages_messages_page_open_linked_page',
                        )}
                      </NavLink>
                    </TovButton>
                  ) : null}
                  {unreadDeliveryIds.has(item.delivery.id) ? (
                    <TovButton
                      variant="secondary"
                      onClick={() => liveUpdates.markRead(item.delivery.id)}
                    >
                      {msg(
                        'pages.messages_page.pages_messages_page_mark_reviewed',
                      )}{' '}
                    </TovButton>
                  ) : null}
                  <TovButton asChild variant="secondary">
                    <NavLink to="/access">
                      {msg('app.router.app_router_review_access')}
                    </NavLink>
                  </TovButton>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.messages_page.pages_messages_page_planner_updates_that_are_relevant_to_your_invite',
            )}{' '}
          </p>
        )}
      </TovCard>
    </section>
  );
}
